/**
 * Sidebar product filter search
 * @module productFilterActiveSearch
 */

import $ from 'jquery';
import mergeOptions from 'styleguide/lib/merge-options';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import { get } from '../lib/props';
import './product-filter-count';

export const PLUGIN_NAME = 'productFilterActiveButton';

export const DEFAULTS = {
  clearAllText: get('rcProps.filter.clearAllText', 'Clear All'),
  filteredByText: get('rcProps.filter.filteredByText', 'Filtered By'),
  mountEl: null,
  layoutType: 'default'
};

const createCloseIcon = () => `<span class="icon icon--x icon--xs u-margin-l--sm">
  <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"
      role="presentation">
      <path
          d="M9.06 8l2.72-2.72A.75.75 0 0 0 10.72 4.22L8 6.94 5.28
             4.22A.75.75 0 0 0 4.22 5.28L6.94 8l-2.72 2.72A.75.75 0 1 0 5.28 11.78L8
             9.06l2.72 2.72a.75.75 0 0 0 1.061-1.061z">
      </path>
  </svg>
</span>`;

const createFilterButton = (text) => `<li class="u-inline-block u-padding-t--md u-padding-r--md">
  <button class="pill-button pill-button--light-grey">
      ${text}
      ${createCloseIcon()}
  </button>
</li>`;

const containerButtonsDefaultLayout = (clearAllText, filteredByText) => {
  return `<div class="u-padding-b--lg u-margin-b--xxl u-border-b u-border--xs u-border--color-lighter">
    <div class="u-flex u-flex--space-between"><h2 class="h3-secondary u-capitalize u-margin-tb--none">${filteredByText}:</h2> <button class="link u-lowercase js-search-multi-filter-clear-all">${clearAllText}</button></div>
    <ul class="u-margin-l--none u-padding-t--xl"></ul>
  </div> `;
}

const containerButtonsPageLayout = (clearAllText, filteredByText) => {
  return `<div class="u-padding-b--lg">
    <div class="u-flex u-flex--space-between"><h2 class="u-text--lg u-weight--normal u-capitalize u-margin-t--md u-margin-b--none">${filteredByText}:</h2></div>
    <ul class="u-margin-l--none u-padding-b--md"><li class="u-inline-block u-padding-t--md u-padding-r--md"><button class="pill-button pill-button--black js-search-multi-filter-clear-all">${clearAllText}</button></li></ul> 
  </div>`;
}

const createContainerButtons = (clearAllText, filteredByText, layoutType) => layoutType === 'default' ? containerButtonsDefaultLayout(clearAllText, filteredByText): containerButtonsPageLayout(clearAllText, filteredByText);

const addButtonsContainer = ($container, $checkboxes, { clearAllText, filteredByText, layoutType }) => {
  const $buttonContainer = $(createContainerButtons(clearAllText, filteredByText, layoutType));
  const $clearAllButton = $buttonContainer.find('button');

  $container.prepend($buttonContainer);

  $clearAllButton.on('click', () => {
    $checkboxes.each((index, checkbox) => {
      if (checkbox.checked) {
        $(checkbox).prop('checked', false);
      }
    });
    $(":button.pill-button", $buttonContainer).remove()
    $("span[id$='-filter-count']", $container).empty()
    checkButtonListVisibility($buttonContainer, $checkboxes);
  });

  return {
    $buttonListContainer: $buttonContainer,
    $buttonList: $buttonContainer.find('ul'),
  };
};

const addButtonToList = ($checkbox, $buttonList, layoutType) => {
  const checkboxText = $checkbox.next().text().split('(')[0];
  const $filterButton = $(createFilterButton(checkboxText));

  if(layoutType === 'default') {
    // insert new button at the end of button list
    $buttonList.append($filterButton);
  } else if(layoutType === 'page') {
    // insert new button before the "clear all" button
    $buttonList.find('.js-search-multi-filter-clear-all').before($filterButton);
  }
  
  $filterButton.on('click', () => {
    $checkbox.prop('checked', false).trigger('change');
  });
  return $filterButton;
};

const countChecked = ($checkboxes) => $checkboxes.filter(':checked').length;

const checkButtonListVisibility = ($buttonListContainer, $checkboxes) => {
  if (countChecked($checkboxes)) {
    $buttonListContainer.removeClass('u-hide');
    return;
  }

  $buttonListContainer.addClass('u-hide');
};

const initActiveButtons = ($buttonListContainer, $buttonList, $checkboxes, {layoutType}) => {
  $checkboxes.each((index, checkbox) => {
    const $checkbox = $(checkbox);
    let $filterButton;

    if (checkbox.checked) {
      $filterButton = addButtonToList($checkbox, $buttonList, layoutType);
    }

    $checkbox.on('change', () => {
      if (checkbox.checked) {
        $filterButton = addButtonToList($checkbox, $buttonList, layoutType);
      } else {
        $filterButton.remove();
      }
      checkButtonListVisibility($buttonListContainer, $checkboxes);
    });
  });
};

/**
 * Initializes productFilterActiveButton.
 * @param {HTMLElement} container - filter container
 * @param {Object} options - Options used for wiring up the plugin
 * @see DEFAULTS
 */
export default function productFilterActiveButton(container, options = {}) {
  const $container = $(container);
  const $countInitEls = $container.find('[data-filter-count]');
  const opts = mergeOptions(DEFAULTS, options, $container);
  // mount by default will be the container but can be overriden by the
  // mountEl property.
  opts.mountEl = options.mountEl ? $(options.mountEl) : $container;
  const $count = $(opts.filterCount);
  const $checkboxes = $container.find('input[type="checkbox"]');
  const { $buttonListContainer, $buttonList } = addButtonsContainer(opts.mountEl, $checkboxes, opts);

  initActiveButtons($buttonListContainer, $buttonList, $checkboxes, opts);
  checkButtonListVisibility($buttonListContainer, $checkboxes);
  $countInitEls.productFilterCount();
}

registerJQueryPlugin(PLUGIN_NAME, productFilterActiveButton);


// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/product-filter-active-button.js